/**
 * Authentication API
 * Handles login, user info, and user management
 */

import axios from 'axios';
import type { AuthUser, LoginResponse, LoginCredentials } from '@/types/auth.types';

// Create a separate axios instance for auth that doesn't use the interceptors
// This prevents circular issues with token refresh
const authClient = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000/api/v1',
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * Login with email and password
 * Sends credentials to backend, receives JWT token
 */
export async function login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
        const response = await authClient.post('/auth/login', credentials);
        return response.data;
    } catch (error: any) {
        return {
            success: false,
            error: {
                code: error.response?.data?.error?.code || 'login-failed',
                message: error.response?.data?.error?.message || 'Login failed',
            },
        };
    }
}

/**
 * Get current user info
 */
export async function getCurrentUser(token: string): Promise<{ success: boolean; data?: { user: AuthUser }; error?: any }> {
    try {
        const response = await authClient.get('/auth/me', {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    } catch (error: any) {
        return {
            success: false,
            error: {
                code: error.response?.data?.error?.code || 'fetch-failed',
                message: error.response?.data?.error?.message || 'Failed to get user info',
            },
        };
    }
}

/**
 * Refresh user permissions
 */
export async function refreshUser(token: string): Promise<{ success: boolean; data?: { user: AuthUser; token?: string }; error?: any }> {
    try {
        const response = await authClient.post('/auth/refresh', {}, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    } catch (error: any) {
        return {
            success: false,
            error: {
                code: error.response?.data?.error?.code || 'refresh-failed',
                message: error.response?.data?.error?.message || 'Failed to refresh user',
            },
        };
    }
}

/**
 * Get available roles and apps
 */
export async function getRolesAndApps(token: string): Promise<any> {
    try {
        const response = await authClient.get('/auth/roles', {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    } catch (error: any) {
        return {
            success: false,
            error: {
                code: 'fetch-failed',
                message: 'Failed to fetch roles',
            },
        };
    }
}

// =============================================================================
// User Management (Super Admin)
// =============================================================================

/**
 * List all dashboard users
 */
export async function listUsers(token: string): Promise<any> {
    try {
        const response = await authClient.get('/auth/users', {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    } catch (error: any) {
        return {
            success: false,
            error: {
                code: error.response?.data?.error?.code || 'fetch-failed',
                message: error.response?.data?.error?.message || 'Failed to list users',
            },
        };
    }
}

/**
 * Create a new dashboard user
 */
export async function createUser(
    token: string,
    userData: { email: string; password: string; displayName?: string; role: string; apps?: string[] }
): Promise<any> {
    try {
        const response = await authClient.post('/auth/users', userData, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    } catch (error: any) {
        return {
            success: false,
            error: {
                code: error.response?.data?.error?.code || 'create-failed',
                message: error.response?.data?.error?.message || 'Failed to create user',
            },
        };
    }
}

/**
 * Update a user
 */
export async function updateUser(
    token: string,
    uid: string,
    updates: { role?: string; apps?: string[]; displayName?: string; disabled?: boolean }
): Promise<any> {
    try {
        const response = await authClient.put(`/auth/users/${uid}`, updates, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    } catch (error: any) {
        return {
            success: false,
            error: {
                code: error.response?.data?.error?.code || 'update-failed',
                message: error.response?.data?.error?.message || 'Failed to update user',
            },
        };
    }
}

/**
 * Delete a user
 */
export async function deleteUser(token: string, uid: string): Promise<any> {
    try {
        const response = await authClient.delete(`/auth/users/${uid}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    } catch (error: any) {
        return {
            success: false,
            error: {
                code: error.response?.data?.error?.code || 'delete-failed',
                message: error.response?.data?.error?.message || 'Failed to delete user',
            },
        };
    }
}
